/**
 * Department Service for managing department operations
 * Handles API calls for department CRUD operations
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { createErrorLogger, ErrorType } from '../utils/logger';

// Department API follows standard /api prefix pattern
const DEPARTMENT_API_BASE = API_BASE_URL;

const logger = createErrorLogger('DepartmentService');

class DepartmentService {
  /**
   * Get all departments
   * @param {boolean} includeInactive - Whether to include inactive departments
   * @returns {Promise<object>} List of departments
   */
  async getAllDepartments(includeInactive = false) {
    logger.info('getAllDepartments', `Fetching departments (includeInactive: ${includeInactive})`);

    try {
      const response = await axios.get(`${DEPARTMENT_API_BASE}/departments`, {
        params: { include_inactive: includeInactive }
      });

      logger.info('getAllDepartments', `Retrieved ${response.data.total} departments`);
      return response.data;
    } catch (error) {
      logger.error('getAllDepartments', 'Error fetching departments', error, ErrorType.API);
      throw error;
    }
  }

  /**
   * Get a specific department by ID
   * @param {number} departmentId - Department ID
   * @returns {Promise<object>} Department details
   */
  async getDepartmentById(departmentId) {
    logger.info('getDepartmentById', `Fetching department ${departmentId}`);

    try {
      const response = await axios.get(`${DEPARTMENT_API_BASE}/departments/${departmentId}`);
      logger.info('getDepartmentById', 'Department retrieved successfully', { departmentId });
      return response.data;
    } catch (error) {
      logger.error('getDepartmentById', 'Error fetching department', error, ErrorType.API, { departmentId });
      throw error;
    }
  }

  /**
   * Create a new department
   * @param {string} name - Department name
   * @param {string} description - Department description
   * @returns {Promise<object>} Creation result
   */
  async createDepartment(name, description = '') {
    logger.info('createDepartment', `Creating department: ${name}`);

    try {
      const response = await axios.post(`${DEPARTMENT_API_BASE}/departments`, {
        name,
        description
      });

      logger.info('createDepartment', 'Department created successfully', { name });
      return response.data;
    } catch (error) {
      logger.error('createDepartment', 'Error creating department', error, ErrorType.API, { name });
      throw error;
    }
  }

  /**
   * Update a department
   * @param {number} departmentId - Department ID
   * @param {object} updateData - Update data
   * @returns {Promise<object>} Update result
   */
  async updateDepartment(departmentId, updateData) {
    logger.info('updateDepartment', `Updating department ${departmentId}`, updateData);

    try {
      const response = await axios.put(`${DEPARTMENT_API_BASE}/departments/${departmentId}`, updateData);
      logger.info('updateDepartment', 'Department updated successfully', { departmentId });
      return response.data;
    } catch (error) {
      logger.error('updateDepartment', 'Error updating department', error, ErrorType.API, { departmentId });
      throw error;
    }
  }

  /**
   * Delete a department
   * @param {number} departmentId - Department ID
   * @returns {Promise<object>} Deletion result
   */
  async deleteDepartment(departmentId) {
    logger.info('deleteDepartment', `Deleting department ${departmentId}`);

    try {
      const response = await axios.delete(`${DEPARTMENT_API_BASE}/departments/${departmentId}`);
      logger.info('deleteDepartment', 'Department deleted successfully', { departmentId });
      return response.data;
    } catch (error) {
      logger.error('deleteDepartment', 'Error deleting department', error, ErrorType.API, { departmentId });
      throw error;
    }
  }

  /**
   * Get user count for a department
   * @param {number} departmentId - Department ID
   * @returns {Promise<object>} User count data
   */
  async getDepartmentUserCount(departmentId) {
    logger.info('getDepartmentUserCount', `Getting user count for department ${departmentId}`);

    try {
      const response = await axios.get(`${DEPARTMENT_API_BASE}/departments/${departmentId}/users/count`);
      logger.info('getDepartmentUserCount', 'User count retrieved', { departmentId, count: response.data.user_count });
      return response.data;
    } catch (error) {
      logger.error('getDepartmentUserCount', 'Error getting user count', error, ErrorType.API, { departmentId });
      throw error;
    }
  }

  /**
   * Get departments formatted for dropdown selection
   * @param {boolean} includeEmpty - Whether to include "No Department" option
   * @returns {Promise<Array>} Array of department options
   */
  async getDepartmentOptions(includeEmpty = true) {
    logger.info('getDepartmentOptions', `Getting department options (includeEmpty: ${includeEmpty})`);

    try {
      const response = await this.getAllDepartments(false); // Only active departments
      const options = response.departments.map(dept => ({
        value: dept.id,
        label: dept.name,
        description: dept.description
      }));

      if (includeEmpty) {
        options.unshift({
          value: null,
          label: 'No Department',
          description: 'No department assigned'
        });
      }

      logger.info('getDepartmentOptions', `Returning ${options.length} department options`);
      return options;
    } catch (error) {
      logger.error('getDepartmentOptions', 'Error getting department options', error, ErrorType.API);
      throw error;
    }
  }

  /**
   * Validate department name availability
   * @param {string} name - Department name to validate
   * @param {number} excludeId - Department ID to exclude from check (for updates)
   * @returns {Promise<boolean>} True if name is available
   */
  async validateDepartmentName(name, excludeId = null) {
    logger.info('validateDepartmentName', `Validating department name: ${name}`);

    try {
      const response = await this.getAllDepartments(true); // Include inactive
      const existingDepartment = response.departments.find(dept =>
        dept.name.toLowerCase() === name.toLowerCase() && dept.id !== excludeId
      );

      const isAvailable = !existingDepartment;
      logger.info('validateDepartmentName', `Name "${name}" is ${isAvailable ? 'available' : 'taken'}`);

      return isAvailable;
    } catch (error) {
      logger.error('validateDepartmentName', 'Error validating department name', error, ErrorType.API, { name });
      throw error;
    }
  }
}

// Create singleton instance
const departmentService = new DepartmentService();

// Export singleton instance
export default departmentService;
